import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import ObjectId from 'bson-objectid';

// Services
import AuthService from '../services/auth.service';
import { getDevice, getDeviceBySerialNumber, getDevicebyObjectId } from '../services/device.service';
import { getPairingInfoByDeviceId } from '../services/pairing.service';
import { getVisit, getVisits } from '../services/visit.service';
import { getNearFacilities } from '../services/facility.service';

// Types
import { PairingInfo } from '../types/PairingInfo';
import { Visit } from '../types/Visit';
import { Coordinates } from '../types/Coordinates';
import { FilterableTypes } from '../types/FilterableTypes';

// Constants
import { getCoordinatesPreset } from '../constants/Constants';
import { VisitReader } from '../types/VisitReader';
import { LatLng } from 'leaflet';
import { DeviceEnriched } from '../types/DeviceEnriched';

interface Location {
  type: string;
  longitude: number;
  latitude: number;
}

const useDeviceDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceEnriched>();
  const [pairingInfo, setPairingInfo] = useState<PairingInfo>();
  const [visitInfo, setVisitInfo] = useState<Visit[]>([]);
  const [positions, setPositions] = useState<Coordinates[]>([]);
  const [visitsLogInfo, setVisitsLogInfo] = useState<Visit[]>([]);
  const [searchParams] = useSearchParams();

  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const bySerial = searchParams.get('bySerial') === 'true';

  const fetchData = useCallback(async () => {
    if (!id) {
      setErrorMessage('Invalid device ID');
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const device = bySerial
        ? (await getDeviceBySerialNumber(id)).results.at(0)
        : ObjectId.isValid(id)
          ? (await getDevicebyObjectId(id)).results.at(0)
          : await getDevice(id);
      if (!device) throw new Error('Could not find device');

      const pairing = isExternalUser ? undefined : (await getPairingInfoByDeviceId(device.deviceId)).data;

      const visits = device.deviceId
        ? (await getVisit({ filters: [{ deviceId: device.deviceId, filterType: FilterableTypes.Equals }] } as any)).data.results
        : [];
      const visitsLog = device.deviceId
        ? (await getVisits({ filters: [{ deviceId: device.deviceId, filterType: FilterableTypes.Equals }] } as any)).data.results
        : [];

      setDeviceInfo(device);
      setPairingInfo(pairing);
      setVisitInfo(visits);
      setVisitsLogInfo(visitsLog);
    } catch (error) {
      setErrorMessage('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [id, bySerial, isExternalUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getNearFacility = useCallback(async (location: Location, closestFacilityIds: string[]) => {
    const closestFacilityId = closestFacilityIds.length ? _.first(closestFacilityIds) : undefined;
    try {
      const res: any = await getNearFacilities({
        lat: location?.latitude ?? 0,
        lng: location?.longitude ?? 0,
        distanceInMeters: 10000,
      });

      if (res.status === 200) {
        const facilities = res.data.results
          .filter((v) => v.geofences && v.geofences?.length > 0)
          .map((fa) => {
            return {
              ...(fa.id === closestFacilityId ? getCoordinatesPreset('CentroidCircle') : getCoordinatesPreset('Facility')),
              geofences: fa.geofences?.map((geof) => {
                const geofence = { ...geof };
                geofence.facilityName = fa.name;
                return geofence;
              }),
              showPolygon: true,
            } as Coordinates;
          });

        setPositions((prevPositions) => {
          const filteredPositions = prevPositions.filter((v) => v._type !== 'Facility');
          return [...filteredPositions, ...facilities];
        });
      }
    } catch (error) {
      setErrorMessage('Failed to fetch near facilities');
    }
  }, []);

  const updateNearReadersPositions = useCallback((readers: VisitReader[]) => {
    if (readers.length > 0) {
      const nearReaders: Coordinates[] = readers
        .filter((n) => n.location)
        .map((r) => ({
          ...(r.showArrowFlag ? getCoordinatesPreset('CentroidCircle') : getCoordinatesPreset('NearReader')),
          latLng: new LatLng(Number(r.location?.latitude), Number(r.location?.longitude)),
          showMarker: r.showArrowFlag,
          showCircleMarker: true,
          circleRadius: 280, // Circle radius in meters
        }));

      setPositions((prevPositions) => {
        const filteredPositions = prevPositions.filter((v) => v._type !== 'NearReader');
        return [...filteredPositions, ...nearReaders];
      });
    }
  }, []);

  return {
    deviceInfo,
    pairingInfo,
    visitInfo,
    visitsLogInfo,
    loading,
    errorMessage,
    positions,
    getNearFacility,
    updateNearReadersPositions,
    refetchDeviceInfo: fetchData,
  };
};

export default useDeviceDetails;
